/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {ColumnState} from 'ag-grid-community';
import produce from 'immer';
import {
  getGridColumnState,
  onSetGridColumns,
  onUpdateGridColumns
} from '@ion/applications/src/store/helpers';
import {PageState} from '@ion/applications/src/types/pages';
import {gridDefaultPageSize} from '@ion/applications/src/utils/helpers';
import {
  DeviceTypes,
  DeviceTypesFilters
} from '@ion/core/src/types/entities';
import {ServiceError} from '@ion/core/src/types/services';
import {AuditRecordType} from '@ion/dls/src/editors/audit/AuditGridUtils';
import {
  AsyncState
} from '@/types/redux';
import {
  ADD_DEVICE_TYPE,
  AUDIT_DEVICE_TYPES,
  AUDIT_DEVICE_TYPES_SHOW_CHANGES_DIALOG,
  GET_DEVICE_TYPES_ERROR,
  GET_DEVICE_TYPES_REQUEST,
  GET_DEVICE_TYPES_RESPONSE,
  INITIALIZE_DEVICE_TYPES,
  RESET_DEVICE_TYPES_SEARCH_QUERIES,
  SET_DEVICE_TYPES_GRID_PAGE_SIZE,
  SET_GRID_COLUMNS,
  UPDATE_DEVICE_TYPES_SEARCH_QUERIES,
  UPDATE_GRID_COLUMNS,
  VIEW_DEVICE_TYPES
} from '../actions/devicetypes';

export interface DeviceTypesAction {
  type: string;
  result?: any;
  error?: ServiceError;
  show?: boolean;
};

export interface DeviceTypeAuditState extends DeviceTypesActionState {
  showChangesDialog?: boolean;
  auditRecord?: AuditRecordType;
};

export interface DeviceTypeAction extends DeviceTypesAction {
  deviceType?: DeviceTypes;
  deviceTypes?: DeviceTypes[];
  searchParams?: DeviceTypesFilters
};

interface DeviceTypesActionState extends AsyncState {
  selected: DeviceTypes | DeviceTypes[] | null;
  result: DeviceTypes | DeviceTypes[] | null;
  isEditing: boolean;
  record?: DeviceTypes | DeviceTypes[] | null;
  isView?: boolean;
};

export interface DeviceTypesState extends PageState {
  devicetypes: {
    error: ServiceError;
    loading: boolean;
  },
  grid: {
    columnState: ColumnState[],
    preferedColumnState: ColumnState[],
    hasUpdate: boolean,
    pageSize: number
  },
  audit: DeviceTypeAuditState,
  view: DeviceTypesActionState,
  search: DeviceTypesFilters,
  add: DeviceTypesActionState
};

// defines the initial state for the reducer ...
export const initialState: DeviceTypesState = {
  isInitialized : false,
  devicetypes   : {
    error   : null,
    loading : false
  },
  grid : {
    columnState         : null,
    preferedColumnState : null,
    hasUpdate           : false,
    pageSize            : gridDefaultPageSize
  },
  view : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  audit : {
    isEditing         : false,
    selected          : null,
    loading           : false,
    error             : null,
    result            : null,
    showChangesDialog : false,
    auditRecord       : null
  },
  search : {},
  add    : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null,
    isView    : false
  }
};
// defines the preference key for the admin device type page ...
export const PREFERENCE = 'schedule-devicetypes';

// defines this reducers reducer functions ...
const reducers = {
  [INITIALIZE_DEVICE_TYPES] : draft => {
    draft.isInitialized            = true;
    const preference               = getGridColumnState(PREFERENCE, true);
    draft.grid.columnState         = preference;
    draft.grid.preferedColumnState = preference;
  },
  [SET_GRID_COLUMNS] : ({grid, devicetypes}: DeviceTypesState, {columnState}) => {
    onSetGridColumns({
      grid,
      isLoading  : devicetypes.loading,
      preference : PREFERENCE
    }, columnState);
  },
  [UPDATE_GRID_COLUMNS] : ({grid}: DeviceTypesState, {columnState}) => {
    onUpdateGridColumns({grid, preference: PREFERENCE}, columnState);
  },
  [GET_DEVICE_TYPES_REQUEST] : ({devicetypes}: DeviceTypesState) => {
    devicetypes.loading = true;
  },
  [GET_DEVICE_TYPES_RESPONSE] : ({devicetypes}: DeviceTypesState) => {
    devicetypes.loading = false;
  },
  [GET_DEVICE_TYPES_ERROR] : ({devicetypes}: DeviceTypesState, {error}: DeviceTypesAction) => {
    devicetypes.error = error;
    devicetypes.loading = false;
  },
  [VIEW_DEVICE_TYPES] : ({view}: DeviceTypesState, {deviceType, show}: DeviceTypeAction) => {
    view.selected = show ? deviceType : null;
    view.isEditing = show;
    view.result = null;
  },
  [AUDIT_DEVICE_TYPES] : ({audit}: DeviceTypesState, {deviceType, show}: DeviceTypeAction) => {
    audit.selected  = show ? deviceType : null;
    audit.isEditing = show;
  },
  [AUDIT_DEVICE_TYPES_SHOW_CHANGES_DIALOG] : ({audit}: DeviceTypesState, {auditRecord, show}) => {
    audit.showChangesDialog = show;
    audit.auditRecord       = auditRecord;
  },
  [UPDATE_DEVICE_TYPES_SEARCH_QUERIES] : ({search}: DeviceTypesState, {searchParams}) => {
    search.name         = searchParams.name || null;
  },
  [RESET_DEVICE_TYPES_SEARCH_QUERIES] : ({search}: DeviceTypesState) => {
    search.name         = null;
  },
  [ADD_DEVICE_TYPE] : ({add}: DeviceTypesState, {show, deviceType}: DeviceTypeAction) => {
    add.isEditing = show;
    add.result = null;
    add.selected = show ? deviceType : null;
    add.isView = !!deviceType;
  },
  [SET_DEVICE_TYPES_GRID_PAGE_SIZE] : ({grid}: DeviceTypesState, {pageSize}) => {
    grid.pageSize = pageSize;
  }
};

// defines all reducers for actions of interest to the this reducer ...
export default produce((draft: DeviceTypesState = initialState, action) => {
  reducers?.[action.type]?.(draft, action);
  return draft;
});
